import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GoalModel, UserModel } from '@bp-core/src/lib/proto/common-message.pb';
import { BaseGoalRequest } from '@bp-core/src/lib/proto/portal-goal.pb';
import { PortalGoalGrpcServiceClient } from '@bp-core/src/lib/proto/portal-goal.pbsc';
import { AppInteractionService } from '@bp-core/src/lib/services/app-interaction.service';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { GoalInfo } from 'bp/modules/goal/models/goal-info.model';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Observable, combineLatest, map, take } from 'rxjs';
import { BpCodes } from '../models/config-code.model';
import { Step } from '../models/first-step.enum';
import { BpConfirmationService } from './bp-confirmation.service';
import { PortalConfigParamService } from './portal-config-param.service';
import { UserStateService } from './user-state.service';
dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * Servicio de utilidades que servirá para realizar acciones comunes
 *
 * @export
 * @class UtilityService
 */
@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private user: UserModel | undefined;
  private firstStepModalMessage: Record<
    | 'goal'
    | 'invest'
    | 'referral'
    | 'vectorContractUserInSteps'
    | 'vectorContract'
    | 'contractUserInSteps'
    | 'bpus'
    | 'dollarbox'
    | 'account'
    | 'defaultPortfolio',
    string
  > = {
    goal: 'Para crear tu primera meta, debes terminar de crear tu cuenta.',
    invest: 'Para poder invertir, primero debes terminar de crear tu cuenta.',
    referral: 'Para recomendar a un amigo, debes terminar de crear tu cuenta.',
    vectorContractUserInSteps:
      'Debes completar los primeros pasos hasta firmar el contrato con vector y tener tu correo verificado.',
    vectorContract: 'Debes de firmar el contrato con vector primero.',
    contractUserInSteps:
      'Debes completar los primeros pasos hasta firmar el contrato y tener tu correo verificado.',
    bpus: 'Para poder activar BetterplanUS debes terminar de crear tu cuenta.',
    dollarbox: 'Para poder activar la Billetera Dólar debes terminar de crear tu cuenta.',
    account: 'Para poder activar esta cuenta debes terminar de crear tu cuenta.',
    defaultPortfolio:
      'Para poder elegir el portafolio por ti necesitamos que primero completes tu cuenta.',
  };
  private stepLabelMap: Record<Step, string> = {
    // [Step.FIRST]: 'Ir al paso 1',
    // [Step.SECOND]: 'Ir al paso 2',
    // [Step.THIRD]: 'Ir al paso 3',
    // [Step.FOURTH]: 'Ir al paso 4',
    [Step.FIRST]: 'Completar cuenta',
    [Step.SECOND]: 'Completar cuenta',
    [Step.THIRD]: 'Completar cuenta',
    [Step.FOURTH]: 'Completar cuenta',
    [Step.DONE]: '',
  };
  constructor(
    private router: Router,
    private userStateService: UserStateService,
    private bpConfirmationService: BpConfirmationService,
    private portalGoalService: PortalGoalGrpcServiceClient,
    private authService: AuthService,
    private appInteractionService: AppInteractionService,
    private configParamService: PortalConfigParamService,
  ) {
    this.authService.getCurrentUser().subscribe(currentUser => {
      this.user = currentUser;
    });
  }

  /**
   * Navegar a la página de deposit considerando si el usuario acabo los pasos o
   * esta en el paso 4
   *
   * @memberof UtilityService
   */
  navigateToInvest(goalId?: number, goalInfo?: GoalInfo): void {
    if (goalId) {
      if (goalInfo?.isGetStartedGoal) {
        this.bpConfirmationService.open({
          title: 'Importante',
          message: 'Para invertir en esta meta, debes confirmar el portafolio.',
          icon: { show: true },
          actions: {
            confirm: {
              label: 'Aceptar',
              function: dialogRef => {
                dialogRef.close();
                this.router.navigate(['/goals', goalId, 'invest']);
              },
            },
            cancel: {
              show: true,
              label: 'Cancelar',
            },
          },
        });
      } else {
        this.router.navigate(['/deposit', goalId]);
      }
      return;
    }
    this.userStateService.currentStep$.pipe(take(1)).subscribe(currentStepInfo => {
      if (currentStepInfo?.currentStep === Step.DONE) {
        this.router.navigate(['/deposit']);
        return;
      }
      if (currentStepInfo?.currentStep === Step.FOURTH) {
        if (currentStepInfo?.canDeposit) {
          this.router.navigate(['/deposit']);
        } else {
          // No puede depositar porque no tiene metas
          // Mostrar modal de que no tiene metas
          this.router.navigate(['/goals/start']);
          //   this.redirectService.redirect(`${environment.urlPortal}/legacy/goals/start`);
        }
        return;
      }

      // Mostramos modal de que no puede depositar porque esta en un paso anterior
      this.createFirstStepConfirmationModal(currentStepInfo?.currentStep, 'invest');
    });
  }

  /**
   * Navigates to the insurance page.
   *
   * This method checks the current step information and navigates to the insurance page if the current step is 'DONE'.
   * If the current step is not 'DONE', it displays a confirmation modal indicating that the user cannot navigate to the insurance page because they are in a previous step.
   */
  navigateToInsurance(): void {
    this.userStateService.currentStep$.pipe(take(1)).subscribe(currentStepInfo => {
      if (currentStepInfo?.currentStep === Step.DONE) {
        this.router.navigate(['/insurance']);
        return;
      }
      // Mostramos modal de que no puede ir a seguros porque esta en un paso anterior
      this.createFirstStepConfirmationModal(currentStepInfo?.currentStep, 'invest');
    });
  }

  /**
   * Navegar a la página de crear meta considerando si el usuario acabo los pasos o
   * esta en el paso 4
   *
   * @param {boolean} [newUrl] Si es true, navega a new, si es false, navega a start
   * @memberof UtilityService
   */
  navigateToNewGoal(newUrl = false): void {
    this.userStateService.currentStep$.pipe(take(1)).subscribe(currentStepInfo => {
      // if (
      //   currentStepInfo?.currentStep === Step.DONE ||
      //   currentStepInfo?.currentStep === Step.FOURTH
      // ) {
      this.router.navigate([`/goals/${newUrl ? 'new' : 'start'}`]);
      // this.redirectService.redirect(
      //   `${environment.urlPortal}/legacy/goals/${newUrl ? 'new' : 'start'}`,
      // );
      //   return;
      // }
      // Mostramos modal de que no puede crear una meta porque esta en un paso anterior
      // this.createFirstStepConfirmationModal(currentStepInfo?.currentStep, 'goal');
    });
  }

  /**
   * Navegar a la página de referidos considerando si el usuario acabo los pasos o
   * esta en el paso 4
   *
   * @memberof UtilityService
   */
  navigateToReferral(): void {
    this.userStateService.currentStep$.pipe(take(1)).subscribe(currentStepInfo => {
      if (
        currentStepInfo?.currentStep === Step.DONE ||
        currentStepInfo?.currentStep === Step.FOURTH
      ) {
        this.router.navigate(['/referral']);
        return;
      }
      // Mostramos modal de que no puede ir a referidos porque esta en un paso anterior
      this.createFirstStepConfirmationModal(currentStepInfo?.currentStep, 'referral');
    });
  }

  navigateToScheduleWithAdvisor(): void {
    combineLatest([
      this.userStateService.currentStep$,
      this.userStateService.getUserAdvisorReassignmentConditions(),
    ])
      .pipe(take(1))
      .subscribe(([currentStepInfo, reassignmentConditions]) => {
        this.appInteractionService.emit({
          eventName: 'agendarAsesoriaPlataforma',
          eventDetail: 'Usuario da click en agendar asesoría',
        });
        if (
          !reassignmentConditions.wasCreatedBeforeAdvisorReassignmentByIncomeAndPatrimony &&
          !reassignmentConditions.hasIncomeAndPatrimony
        ) {
          this.router.navigate(['/schedule-advisor']);
        } else {
          if (
            currentStepInfo?.currentStep !== Step.DONE &&
            !this.user?.firstSteps?.scheduledAdvisor?.value
          ) {
            this.router.navigate(['/schedule-advisor/calendly']);
          } else {
            this.router.navigate(['/advisor/schedule']);
          }
        }
      });
  }

  /**
   * Filter if the user has the first steps and the vector contract
   * before call a next function
   *
   * @param {() => any} next
   * @memberof UtilityService
   */
  vectorContractSignedAndApprovedFilter(
    next: () => any,
    firstStepModalMessage?:
      | 'goal'
      | 'invest'
      | 'referral'
      | 'vectorContractUserInSteps'
      | 'vectorContract'
      | 'contractUserInSteps'
      | 'bpus'
      | 'account'
      | 'dollarbox'
      | 'defaultPortfolio',
  ): void {
    combineLatest([this.userStateService.currentStep$, this.userStateService.financialEntityInfo$])
      .pipe(take(1))
      .subscribe({
        next: ([currentStepInfo, financialEntityInfo]) => {
          // Check user first steps
          if (
            currentStepInfo?.currentStep === Step.DONE ||
            currentStepInfo?.currentStep === Step.FOURTH
          ) {
            // Check if has vector contract
            const vectorContractSignedAndApproved = financialEntityInfo.signedContractsEntity?.find(
              entity => {
                return (
                  entity.userFinancialEntity?.financialEntity?.uuid === 'vector' &&
                  entity.contract?.signed
                );
              },
            )
              ? true
              : false;

            if (!vectorContractSignedAndApproved) {
              // Show firm-vector-contract modal
              this.bpConfirmationService.open({
                title: `Hola ${this.user?.preferedName}`,
                message: this.firstStepModalMessage[firstStepModalMessage || 'vectorContract'],
                icon: { show: true, name: 'feather:alert-circle' },
                actions: {
                  confirm: {
                    label: 'Firmar contrato',
                    function: dialogRef => {
                      dialogRef.close();
                      this.router.navigate(['/contract/person']);
                    },
                  },
                },
                dismissible: true,
              });
            } else {
              // Call the next function
              next();
            }
          } else {
            // Show make-the-steps modal
            this.createFirstStepConfirmationModal(
              currentStepInfo?.currentStep,
              firstStepModalMessage || 'vectorContractUserInSteps',
            );
          }
        },
      });
  }

  thirdStepFilter(next: () => any): void {
    combineLatest([this.userStateService.currentStep$])
      .pipe(take(1))
      .subscribe({
        next: ([currentStepInfo]) => {
          // Check user first steps
          if (
            currentStepInfo?.currentStep === Step.DONE ||
            currentStepInfo?.currentStep === Step.FOURTH
          ) {
            next();
          } else {
            // Show make-the-steps modal
            this.createFirstStepConfirmationModal(
              currentStepInfo?.currentStep,
              'contractUserInSteps',
            );
          }
        },
      });
  }

  archiveGoal(goal: GoalModel): void {
    let message =
      'Te recordamos que al borrar tu meta dejarás de tener acceso a ella y a todos sus movimientos asociados. Esta acción es irreversible.';
    if (
      this.user?.goals?.filter(
        g =>
          g.state == 'ready' &&
          g.id !== goal.id &&
          g.financialEntityId?.value == goal.financialEntityId?.value,
      ).length! > 0 &&
      goal.starred
    ) {
      const newStarredGoal = this.user?.goals
        ?.filter(
          g =>
            g.state == 'ready' &&
            g.id !== goal.id &&
            g.financialEntityId?.value == goal.financialEntityId?.value,
        )
        .sort((a, b) => a.id! - b.id!)[0];

      message =
        'Te recordamos que al borrar tu meta dejarás de tener acceso a ella y a todos sus movimientos asociados. Dado que se trata de tu meta favorita, “' +
        newStarredGoal?.title +
        '” pasará a ser tu nueva meta favorita, puedes cambiarla cuando lo desees haciendo click sobre las estrellas de cada meta.';
    }
    this.bpConfirmationService.open({
      title: 'Esta acción es irreversible',
      message,
      actions: {
        confirm: {
          color: 'warn',
          label: 'Borrar meta',
          function: dialogRef => {
            this.portalGoalService
              .archiveGoal(new BaseGoalRequest({ goalId: goal.id }))
              .pipe(take(1))
              .subscribe({
                next: response => {
                  dialogRef.close();
                  this.bpConfirmationService.open({
                    title: 'Meta borrada',
                    message: 'Tu meta ha sido borrada exitosamente.',
                    actions: {
                      confirm: {
                        label: 'Entendido',
                        function: dialogRef => {
                          this.authService.refreshUser().subscribe(() => {
                            dialogRef.close();
                            this.router.navigate(['/dashboard']);
                          });
                        },
                      },
                    },
                  });
                },
                error: error => {
                  dialogRef.close();
                  let message = '';
                  switch (error.metadata.get('bp-error-code')) {
                    case 'BP-GRPC-PGS-AG-00003':
                      message = 'No es posible borrar la meta porque aun tiene capital disponible.';

                      break;
                    case 'BP-GRPC-PGS-AG-00002':
                      message =
                        'No es posible borrar la meta porque tiene transacciones pendientes.';
                      break;

                    case 'BP-GRPC-PGS-AG-00001':
                      message = 'No existe la meta.';
                      break;

                    default:
                      message = 'Ocurrió un error al borrar la meta.';

                      break;
                  }
                  this.bpConfirmationService.open(
                    {
                      title: 'Error',
                      message,
                    },
                    'error',
                  );
                },
              });
          },
        },
        cancel: {
          label: 'Cancelar',
          show: true,
        },
      },
    });
  }

  /**
   * Scroll hasta el top de la página de manera suave para todos los navegadores
   * y sin librerías externas
   *
   * @memberof UtilityService
   */
  scrollToTopSmoothly() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
  /**
   * Scroll smoothly to a specific element identified by its ID.
   *
   * @param {string} id - The ID of the element to scroll to.
   */
  scrollSmoothly(id: string, factor = 1) {
    const element = document.getElementById(id);
    if (element) {
      const elementTop = (element.getBoundingClientRect().top + window.pageYOffset) / factor;
      const viewportHeight = window.innerHeight;
      const threshold = viewportHeight * 0.35; // 25% de la altura del viewport
      const centerComfortZone = viewportHeight * 0.2; // 20% del viewport como zona de confort

      // Calcular la posición del elemento en relación con el centro de la pantalla
      const distanceFromCenter = Math.abs(elementTop - (window.pageYOffset + viewportHeight / 2));

      if (distanceFromCenter <= centerComfortZone) {
        // Si el elemento está dentro de la zona de confort, no hacer scroll
        return;
      } else if (elementTop < threshold) {
        // Si el elemento está muy arriba, hacer scroll hasta arriba
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        // Si el elemento está fuera de la zona de confort, hacer scroll suave hasta él
        window.scrollTo({
          top: elementTop - threshold,
          behavior: 'smooth',
        });
      }
    }
  }

  scrollToElementIfNeeded(id: string) {
    const element = document.getElementById(id);
    if (!element) {
      console.warn(`No element found with ID: ${id}`);
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const offsetToCenter = window.innerHeight / 2 - elementRect.height / 2;
    const elementTopRelativeToViewport = elementRect.top;
    const elementBottomRelativeToViewport = elementRect.bottom;

    // Verificar si el elemento está completamente fuera del viewport
    const isElementCompletelyAbove = elementBottomRelativeToViewport < 0;
    const isElementCompletelyBelow = elementTopRelativeToViewport > window.innerHeight;

    // Verificar si el elemento está parcialmente visible pero no idealmente posicionado
    const isElementPartiallyVisible =
      elementTopRelativeToViewport < window.innerHeight && elementBottomRelativeToViewport >= 0;
    const isElementNotCentered =
      elementTopRelativeToViewport > offsetToCenter ||
      elementBottomRelativeToViewport < window.innerHeight - offsetToCenter;

    if (
      isElementCompletelyAbove ||
      isElementCompletelyBelow ||
      (isElementPartiallyVisible && isElementNotCentered)
    ) {
      const absoluteElementTop = window.pageYOffset + elementRect.top;
      const newScrollTop = absoluteElementTop - offsetToCenter;

      window.scrollTo({
        top: newScrollTop,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Crea el modal de confirmación de los primeros pasos
   *
   * @private
   * @param {Step} currentStep
   * @param {('goal' | 'invest' | 'referral')} type
   * @memberof UtilityService
   */
  private createFirstStepConfirmationModal(
    currentStep: Step,
    type:
      | 'goal'
      | 'invest'
      | 'referral'
      | 'vectorContractUserInSteps'
      | 'vectorContract'
      | 'contractUserInSteps'
      | 'bpus'
      | 'dollarbox'
      | 'account'
      | 'defaultPortfolio',
  ): void {
    this.bpConfirmationService
      .open({
        title: `Hola ${this.user?.preferedName}`,
        message: this.firstStepModalMessage[type],
        icon: { show: true, name: 'feather:alert-circle' },
        actions: {
          confirm: {
            label: this.stepLabelMap[currentStep],
            function: dialogRef => {
              dialogRef.close();
              this.router.navigate(['/onboarding']);
            },
          },
        },
        dismissible: true,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.scrollToTopSmoothly();
      });
  }

  /**
   * Oculta o muestra el chat de intercom
   *
   * @param {boolean} visible
   * @memberof UtilityService
   */
  setIntercomChatVisible(visible: boolean): void {
    (window as any).Intercom('update', { hide_default_launcher: !visible });
  }

  searchForActiveGoalInUsersGoalsByCategory(
    goalCategoryCode: string,
    currencyCode?: string,
  ): Observable<boolean> {
    return this.authService.getCurrentUser().pipe(
      take(1),
      map(user => {
        if (
          user?.goals?.find(g => {
            return (
              g.goalCategory?.code === goalCategoryCode &&
              !g.archived &&
              (g.currency?.currencyCode === currencyCode || currencyCode === null)
            );
          })
        ) {
          return true;
        } else {
          return false;
        }
      }),
    );
  }
  canOperateSpot() {
    return combineLatest([
      this.configParamService.get(BpCodes.spotOpenTime), // Hora desde Chile (Ej. "09:30")
      this.configParamService.get(BpCodes.spotCloseTime), // Hora desde Chile (Ej. "13:30")
      this.configParamService.get(BpCodes.forceCloseSpotDate), // Forzar cierre del spot en una fecha específica
    ]).pipe(
      take(1),
      map(([openTime, closeTime, forceCloseSpotDate]) => {
        const userTimeZone = dayjs.tz.guess(); // Detectar zona horaria del cliente
        const today = dayjs().tz(userTimeZone); // Obtener la fecha actual con zona horaria del cliente
        const forceCloseSpot = forceCloseSpotDate && dayjs(forceCloseSpotDate).isSame(today, 'day');

        // Convertir las horas desde Chile a la zona horaria del cliente, ajustando la fecha actual
        const openTimeInUserTZ = dayjs
          .tz(`${today.format('YYYY-MM-DD')} ${openTime}`, 'America/Santiago')
          .tz(userTimeZone)
          .toDate();

        const closeTimeInUserTZ = dayjs
          .tz(`${today.format('YYYY-MM-DD')} ${closeTime}`, 'America/Santiago')
          .tz(userTimeZone)
          .toDate();

        // Calcular si el usuario puede operar según la hora local
        const canOperate = forceCloseSpot
          ? false
          : this.canOperateNow(openTimeInUserTZ, closeTimeInUserTZ);

        return {
          openTime: openTimeInUserTZ,
          closeTime: closeTimeInUserTZ,
          canOperate: canOperate,
        };
      }),
    );
  }
  private canOperateNow(spotOpenTime: Date, spotCloseTime: Date): boolean {
    const now = new Date(); // Hora actual en la zona horaria local del cliente
    const dayOfWeek = now.getDay(); // 0 (domingo) - 6 (sábado)

    // Validar que sea de lunes (1) a viernes (5)
    if (dayOfWeek < 1 || dayOfWeek > 5) {
      return false;
    }

    return now >= spotOpenTime && now <= spotCloseTime;
  }
}
